/**
 * Contact quality: the four words, their kΩ figures, and the readiness gate.
 *
 * THIS FILE IS THE ONE PLACE FOR THE NUMBERS. `calibrations/docs/contact-quality.md`
 * quotes them, records who decided them and why, and must be updated with this
 * file. Ported from `calibrations/static/capture/quality.mjs` (Sprint 1.9.7); the
 * backend never learns these figures.
 *
 * Figures (Sprint 1.9.4, provisional — decided by Enrico Ferrea 2026-09-22,
 * pending a hardware check on a BrainBit Pro):
 *   Good        <  500 kΩ
 *   Usable      500 – 1000 kΩ
 *   Weak        1000 – 2000 kΩ
 *   No contact  ≥ 2000 kΩ, or no reading (NaN / null)
 *
 * BrainBit's dry electrodes read in the hundreds of kΩ (a real 8-channel session
 * read 24–1025 kΩ); clinical wet-EEG figures (50/150 kΩ) are unreachable on this
 * hardware.
 */

export const THRESHOLDS_KOHM = Object.freeze({
  good: 500, // strictly below → Good
  usable: 1000, // strictly below → Usable
  weak: 2000, // strictly below → Weak; at or above → No contact
})

export type Tier = 'good' | 'ok' | 'weak' | 'none'

export const TIERS: readonly Tier[] = Object.freeze(['good', 'ok', 'weak', 'none'])

export const WORDS: Readonly<Record<Tier, string>> = Object.freeze({
  good: 'Good',
  ok: 'Usable',
  weak: 'Weak',
  none: 'No contact',
})

/**
 * Representative kΩ for a tier, for contact quality ESTIMATED FROM THE SIGNAL
 * rather than measured (the classic BrainBit's fallback when its resistance
 * readout fails, R5). The figures sit in the middle of each band so `tierOf`
 * round-trips them; the screen says "estimated" beside them and never prints
 * them as a measurement. A MEASURED classic reading is the vendor's Ω / 1000,
 * read against THRESHOLDS_KOHM above like the Pro's (`docs/classic-resistance.md`).
 */
export const REPRESENTATIVE_KOHM: Readonly<Record<Tier, number>> = Object.freeze({
  good: 300,
  ok: 750,
  weak: 1500,
  none: Number.NaN,
})

// Timing constants for the fitting gate and the guidance rhythm. Canvas 05
// carries HOLD_MS and GUIDANCE_MIN_MS; STALE_AFTER_MS is the app's decision
// (the canvas showed "14 s ago" as an illustration, not a threshold).
export const STALE_AFTER_MS = 5000 // a reading older than this is not current
export const HOLD_MS = 3000 // the gate must hold this long before Ready
export const GUIDANCE_MIN_MS = 3000 // advice changes at most this often
/**
 * The recording length. 180 s, signed off 2026-09-23 (PRODUCT.md §5.2): a
 * 60 s recording yields at most two of the four clean 30 s bins that normal
 * confidence needs. 45,000 rows at 250 Hz, under the backend's 60,000 cap.
 */
export const RECORD_SECONDS = 180
/** "3 minutes" — for copy; the timer format lives in phase.ts. */
export const RECORD_LENGTH_TEXT = RECORD_SECONDS % 60 === 0 ? `${RECORD_SECONDS / 60} minutes` : `${RECORD_SECONDS} seconds`
export const RECORD_TIMER_TEXT = `${Math.floor(RECORD_SECONDS / 60)}:${String(RECORD_SECONDS % 60).padStart(2, '0')}`
export const SAMPLING_RATE = 250

/** kΩ → tier. Anything that is not a finite number is "none". */
export function tierOf(kohm: unknown): Tier {
  if (kohm == null) return 'none'
  const v = typeof kohm === 'number' ? kohm : Number(kohm)
  if (!Number.isFinite(v)) return 'none'
  if (v < THRESHOLDS_KOHM.good) return 'good'
  if (v < THRESHOLDS_KOHM.usable) return 'ok'
  if (v < THRESHOLDS_KOHM.weak) return 'weak'
  return 'none'
}

/** Tier rank for comparisons: good 3 · ok 2 · weak 1 · none 0. */
export function rankOf(tier: Tier | string): number {
  return ({ good: 3, ok: 2, weak: 1, none: 0 } as Record<string, number>)[tier] ?? 0
}

/**
 * The readiness gate over an array of tiers (one per hardware contact):
 * every contact at least Weak, and at least N−1 of N at least Usable.
 * An empty array is never ready.
 */
export function okSet(tiers: readonly Tier[]): boolean {
  if (!Array.isArray(tiers) || tiers.length === 0) return false
  const n = tiers.length
  const allWeakOrBetter = tiers.every((t) => rankOf(t) >= 1)
  const usable = tiers.filter((t) => rankOf(t) >= 2).length
  return allWeakOrBetter && usable >= Math.max(1, n - 1)
}

/** Count of contacts at Usable or better — for "7 of 8" copy. */
export function heldCount(tiers: readonly Tier[]): number {
  return tiers.filter((t) => rankOf(t) >= 2).length
}
